import {Button, Card, FormControl, FormField, IconButton} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn} from 'react-hook-form';

import {indexBy} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {CustomerSelectSearch, SaneCustomer, usePhoneNumbers} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useGetEmptyAuthorizedPerson} from '../../../../../hooks/useGetEmptyAuthorizedPerson';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

interface AuthorizedPersonsProps extends RequiredTestIdProps {
  control: FormControl<ReturnVehicleFormValues>;
  formApi: UseFormReturn<ReturnVehicleFormValues>;
}

export function AuthorizedPersons(props: AuthorizedPersonsProps) {
  const {countriesOptions} = usePhoneNumbers();

  const {append, fields, remove} = useFieldArray({
    name: 'authorizedPersons',
    control: props.control,
  });

  const setRequiredValue: typeof props.formApi.setValue = (path, value) => {
    const isValueInvalid = isNilOrEmpty(value);

    if (isValueInvalid) {
      props.formApi.setError(path, {
        type: 'required',
        message: i18n.t('general.validations.fieldIsRequired'),
      });
    }

    props.formApi.setValue(path, value, {shouldValidate: !isValueInvalid});
  };

  const handleCustomerSelect = (index: number) => (customer: SaneCustomer) => {
    const identityCardsByType = indexBy((item) => item.type ?? item.id, customer.identityCards);

    const drivingLicenseNumber = identityCardsByType?.ID_DRIVER_LICENSE?.cardNumber;
    const idCardNumber = identityCardsByType?.ID_IDENTITY_CARD?.cardNumber;

    setRequiredValue(`authorizedPersons.${index}.name`, customer.name);
    props.formApi.setValue(`authorizedPersons.${index}.idCardNumber`, idCardNumber ?? null);
    props.formApi.setValue(
      `authorizedPersons.${index}.drivingLicenceNumber`,
      drivingLicenseNumber ?? null
    );
    setRequiredValue(`authorizedPersons.${index}.phoneNumber`, {
      countryCode: customer.countryCode,
      number: customer.phoneNumber,
      prefix: customer.prefix,
    });
  };

  const emptyAuthorizedPerson = useGetEmptyAuthorizedPerson();

  return (
    <Card variant="inlineGrey" title={i18n.t('page.rentalOrder.labels.authorizedPersons')}>
      <VStack spacing={4}>
        {fields.map((field, index) => (
          <VStack spacing={4} key={field.id}>
            <CustomerSelectSearch
              onCustomerSelect={handleCustomerSelect(index)}
              data-testid={suffixTestId(`authorizedPersons.${index}.customerSelect`, props)}
            />

            <HStack spacing={2}>
              <Box flex={1}>
                <FormField
                  control={props.control}
                  type="text"
                  name={`authorizedPersons.${index}.name`}
                  onChange={() => props.formApi.clearErrors(`authorizedPersons.${index}.name`)}
                  label={i18n.t('page.rentalOrder.labels.authorizedPerson')}
                  data-testid={suffixTestId(`authorizedPersons.${index}.name`, props)}
                  isRequired
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={props.control}
                  type="phone"
                  countries={countriesOptions}
                  name={`authorizedPersons.${index}.phoneNumber`}
                  onChange={() =>
                    props.formApi.clearErrors(`authorizedPersons.${index}.phoneNumber`)
                  }
                  label={i18n.t('general.labels.phone')}
                  data-testid={suffixTestId(`authorizedPersons.${index}.phoneNumber`, props)}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={props.control}
                  type="text"
                  onChange={() =>
                    props.formApi.clearErrors(`authorizedPersons.${index}.idCardNumber`)
                  }
                  name={`authorizedPersons.${index}.idCardNumber`}
                  label={i18n.t('page.rentalOrder.labels.idCardNumber')}
                  data-testid={suffixTestId(`authorizedPersons.${index}.idCardNumber`, props)}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={props.control}
                  type="text"
                  onChange={() =>
                    props.formApi.clearErrors(`authorizedPersons.${index}.drivingLicenceNumber`)
                  }
                  name={`authorizedPersons.${index}.drivingLicenceNumber`}
                  label={i18n.t('page.rentalOrder.labels.drivingLicenceNumber')}
                  data-testid={suffixTestId(
                    `authorizedPersons.${index}.drivingLicenceNumber`,
                    props
                  )}
                />
              </Box>

              <VStack>
                <Space vertical={5} />
                <IconButton
                  icon="action/delete"
                  severity="danger"
                  isDisabled={fields.length <= 1}
                  onClick={() => remove(index)}
                  data-testid={suffixTestId(`authorizedPersons.${index}.delete`, props)}
                />
              </VStack>
            </HStack>
          </VStack>
        ))}
      </VStack>

      <Space vertical={2} />

      <Button
        variant="link"
        leftIcon="content/add_circle"
        onClick={() => append(emptyAuthorizedPerson)}
        title={i18n.t('page.rentalOrder.labels.addAnotherPerson')}
        data-testid={suffixTestId('addAnotherPerson', props)}
      />
    </Card>
  );
}
